// @ts-strict-ignore
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  ButtonSize,
  ButtonVariant,
  TextArea,
} from '@usealloy/component-library';

import { selectWorkflowData } from 'flow/store/redux/selectors';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { IsolatedEditorContainer } from '../../../styles';
import { RuleEditorContext } from '../../NodeEditorDrawer/context';
import { updateThreshold } from '~/dashboard/views/workflows/store/redux/slice';
import { THRESHOLD_LIMIT } from '../../MenuBar/MenuBarModals/ValidateAndSaveVersionModals/validateRegex';
import ValidityBadge from './ValidityBadge';
import { ThresholdRightAttributeOrigin } from '../../NodeEditorDrawer/types';
import { addTrailingSpaces, scrubTrailingSpaces } from '../RuleThresholdDropdown/helpers';
import { ThresholdContext } from '../context';
import { selectValidator } from './helpers';
import { BEST_EFFORT_PARSING_DEFAULT_STRING } from 'flow/service/thresholds';

function IsolatedTextEditingContainer() {
  const dispatch = useAppDispatch();

  const { thresholds } = useAppSelector(selectWorkflowData);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const { leftDataType } = useContext(ThresholdContext);

  const {
    isolatedEditingBlock,
    setIsolatedEditingBlock,
  } = useContext(RuleEditorContext);

  const { originPublicId, originType } = isolatedEditingBlock;

  const isRegexEditor = (
    originType === ThresholdRightAttributeOrigin.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REGEX
  );
  const isListInclusionEditor = (
    // requires csv style comma delineation format handling
    originType === ThresholdRightAttributeOrigin.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_LIST_INCLUSION
  );
  const supportsValidityChecks = isRegexEditor || (
    isListInclusionEditor && (leftDataType === 'decimal' || leftDataType === 'integer')
  );

  const stringifiedValue = thresholds[originPublicId]
    ?.right.attributeValue === BEST_EFFORT_PARSING_DEFAULT_STRING
    ? '' : String(thresholds[originPublicId]?.right.attributeValue);

  const originalTextValue = isListInclusionEditor
    ? addTrailingSpaces(stringifiedValue) // add trailing spaces to comma delineated string
    : stringifiedValue;

  const [textValue, setTextValue] = useState<string>(originalTextValue);
  const [isValid, setIsValid] = useState<boolean>(true);

  useEffect(() => textAreaRef?.current?.focus(), []);

  useEffect(() => {
    const dispatchedValue = isListInclusionEditor
      ? scrubTrailingSpaces(textValue)
      : textValue;

    dispatch(updateThreshold({
      path: `${isolatedEditingBlock.originPublicId}.right.attributeValue`,
      value: dispatchedValue.trim(),
    }));
  }, [dispatch, isListInclusionEditor, isolatedEditingBlock, textValue]);

  const clearIsolatedEditorState = useCallback(() => (
    setIsolatedEditingBlock({
      originPublicId: undefined,
      originType: undefined,
      originCaretIndex: undefined,
    })
  ), [setIsolatedEditingBlock]);

  useEffect(() => {
    const listenEnterKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isValid) clearIsolatedEditorState();
    };

    document.addEventListener('keydown', listenEnterKey);

    return () => {
      document.removeEventListener('keydown', listenEnterKey);
    };
  }, [clearIsolatedEditorState, isValid]);

  const textLength = isListInclusionEditor
    ? scrubTrailingSpaces(textValue).length
    : textValue.length;

  const textEditLimitErrors = textLength >= THRESHOLD_LIMIT
    ? `${textValue.length > THRESHOLD_LIMIT ? 'Exceeds' : 'Reached'} the maximum character limit of ${THRESHOLD_LIMIT} characters.`
    : null;

  // scrub pasted input newline delineators. Not safe for string lists or regex strings
  const scrubNewlines = (text: string) => text.replace(/[\r\n]+/g, '');
  const getTextValue = (text: string) => (
    leftDataType !== 'string'
      ? scrubNewlines(text)
      : text
  );

  const validator = selectValidator(originType, leftDataType);

  const onInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nativeEvent = e.nativeEvent as InputEvent;
    if (nativeEvent.inputType === 'insertLineBreak') {
      return;
    }

    const text = getTextValue(e.target.value);
    setTextValue(text);

    if (supportsValidityChecks) {
      const isNewValueValid = validator(text);
      if (isNewValueValid !== isValid) setIsValid(isNewValueValid);
    }
  };

  return (
    <IsolatedEditorContainer>
      <TextArea
        style={{
          border: 'none',
          boxShadow: 'none',
          color: isRegexEditor ? 'var(--cl-pink-600)' : 'black',
          fontFamily: 'var(--cl-font-family-mono)',
          fontWeight: isRegexEditor ? '' : 'bold',
          paddingLeft: '0',
        }}
        ref={textAreaRef}
        value={textValue}
        error={textEditLimitErrors}
        onInput={onInput}
      />
      <Box
        justifyContent="flex-end"
        gap={2}
        style={{ padding: 'var(--cl-space-1)' }}
      >
        {supportsValidityChecks && <ValidityBadge isValid={isValid} />}
        <Button
          disabled={!isValid}
          variant={ButtonVariant.primary}
          size={ButtonSize.small}
          onClick={clearIsolatedEditorState}
          style={{
            fontFamily: 'var(--cl-font-family-default)',
            margin: 'var(--cl-space-1)',
          }}
        >
          Done
        </Button>
      </Box>
    </IsolatedEditorContainer>
  );
}

export default IsolatedTextEditingContainer;
